import axios from 'axios';
import { BACKEND_BASE_URI } from '../utils/constants';

export interface ClientConfig {
  google_client_id: string;
  mcp_api_url: string;
  chatwoot_base_url: string;
  chatwoot_website_token: string;
}

// Fetch public config from backend (Google client ID, MCP API URL, etc.)
export const fetchClientConfig = async (): Promise<ClientConfig> => {
  const res = await axios.get(`${BACKEND_BASE_URI}auth/client_config/`);
  return res.data;
};

export interface SocialSignInPayload {
  token: string;
  auth_type: 'GOOGLE' | 'FACEBOOK';
}

export interface UserDetails {
  user_id: number;
  name: string;
  bio: string;
  date_joined: string;
  email: string;
  headline: string;
  image: string | null;
  is_verified: boolean;
  is_expert: boolean;
  languages: string[];
  phone_number: string | null;
  sebi_qualification: string;
  sebi_reg_number: string;
  user_exists: boolean;
  years_of_xp: number;
  company_associated?: boolean;
  company_name?: string;
  // Subscription & wallet fields
  subscription_tier?: string;
  subscription_expires?: string | null;
  copilot_queries_used?: number;
  copilot_queries_reset_at?: string | null;
  wallet_balance?: string;
}

// Social sign in (Google/Facebook)
export const handlePostLoginData = async (
  token: string,
  auth_type: 'GOOGLE' | 'FACEBOOK'
): Promise<any> => {
  try {
    const payload: SocialSignInPayload = {
      token,
      auth_type,
    };
    const url = `${BACKEND_BASE_URI}auth/social_signin/`;
    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error calling auth API:', error);
    throw error;
  }
};

// Fetch user details
export const fetchUserData = async (
  token: string,
  user_id: number | null = null
): Promise<UserDetails> => {
  try {
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    let url = '';
    if (user_id === null) {
      url = `${BACKEND_BASE_URI}auth/user_details/`;
    } else {
      url = `${BACKEND_BASE_URI}auth/user_details?id=${user_id}`;
    }

    const response = await axios.get(url, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
};

// Update user details
export const updateUserData = async (
  token: string,
  payload: FormData
): Promise<UserDetails> => {
  try {
    const headers = {
      Authorization: `Bearer ${token}`,
    };
    const url = `${BACKEND_BASE_URI}auth/user_details/`;
    const response = await axios.post(url, payload, { headers });
    return response.data;
  } catch (error) {
    console.error('Error updating user data:', error);
    throw error;
  }
};

// Traditional email/password login
export const handleEmailLogin = async (
  email: string,
  password: string
): Promise<any> => {
  try {
    const payload = {
      email,
      password,
    };
    const url = `${BACKEND_BASE_URI}auth/signin/`;
    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error during email login:', error);
    throw error;
  }
};

// Email/password registration
export const handleEmailSignUp = async (
  email: string,
  password: string,
  name: string
): Promise<any> => {
  try {
    const payload = {
      email,
      password,
      name,
    };
    const url = `${BACKEND_BASE_URI}auth/signup/`;
    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error during email signup:', error);
    throw error;
  }
};

// Register with phone number (new user signup)
export const registerWithPhone = async (phone_number: string): Promise<any> => {
  const url = `${BACKEND_BASE_URI}auth/register_with_phone/`;
  const response = await axios.post(url, { phone_number });
  return response.data;
};

// Phone OTP Authentication (matching mobile app endpoints)
export const requestOTP = async (phone_number: string): Promise<any> => {
  try {
    const payload = {
      phone_number,
    };
    const url = `${BACKEND_BASE_URI}auth/request_otp/`;
    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error requesting OTP:', error);
    throw error;
  }
};

export const validateOTP = async (
  phone_number: string,
  otp: string
): Promise<any> => {
  try {
    const payload = {
      phone_number,
      otp,
    };
    const url = `${BACKEND_BASE_URI}auth/validate_otp/`;
    const response = await axios.post(url, payload);
    return response.data; // Returns { access, user_exists }
  } catch (error) {
    console.error('Error validating OTP:', error);
    throw error;
  }
};

// Legacy OTP functions (keeping for backward compatibility)
export const sendOTP = requestOTP;
export const verifyOTP = validateOTP;

// Submit support ticket
export const submitSupportTicket = async (
  token: string,
  payload: { subject: string; category: string; description: string }
): Promise<any> => {
  const headers = { Authorization: `Bearer ${token}` };
  const url = `${BACKEND_BASE_URI}auth/support_ticket/`;
  const response = await axios.post(url, payload, { headers });
  return response.data;
};

// Reset password
export const resetPassword = async (
  email: string,
  new_password: string,
  otp: string
): Promise<any> => {
  try {
    const payload = {
      email,
      new_password,
      otp,
    };
    const url = `${BACKEND_BASE_URI}auth/reset_password/`;
    const response = await axios.post(url, payload);
    return response.data;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};
